import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Users, Shield, MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";

const Contact = () => {
  const contactMethods = [
    {
      icon: <Phone className="h-8 w-8 text-blue-900" />,
      title: "Phone",
      primary: "(269) 248-3514",
      description: "Call us for immediate assistance or to schedule transportation",
      available: "During business hours for fastest response"
    },
    {
      icon: <Mail className="h-8 w-8 text-blue-900" />,
      title: "Email",
      primary: "qualitytransportservices42@gmail.com",
      description: "Send us detailed requests or questions via email",
      available: "We respond within 24 hours"
    },
    {
      icon: <MessageCircle className="h-8 w-8 text-blue-900" />,
      title: "Online Booking",
      primary: "Book a Ride Form",
      description: "Use our convenient online form to schedule transportation",
      available: "Available 24/7 for your convenience"
    }
  ];

  const businessHours = [
    { days: "Monday - Thursday", hours: "8:00 AM - 5:00 PM" },
    { days: "Friday", hours: "8:00 AM - 4:00 PM" },
    { days: "Saturday - Sunday", hours: "8:00 AM - 12:00 PM" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Contact Us</h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Get in touch with Quality Transport Services for all your Non-Emergency Medical 
              Transportation needs. We're here to help you get where you need to go safely and comfortably.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">How to Reach Us</h2>
            <p className="text-lg text-gray-600">
              Choose the contact method that works best for you. We're committed to responding promptly.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {contactMethods.map((method, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="mx-auto w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                    {method.icon}
                  </div>
                  <CardTitle className="text-xl">{method.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="font-semibold text-lg text-blue-900">{method.primary}</p>
                  <p className="text-gray-600">{method.description}</p>
                  <p className="text-sm text-gray-500">{method.available}</p>
                  {method.title === "Online Booking" && (
                    <Button asChild className="mt-4 bg-blue-900 hover:bg-blue-800">
                      <Link to="/book-ride">Book a Ride</Link>
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Business Information */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Business Hours */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Clock className="h-6 w-6 text-blue-900" />
                  <span>Business Hours</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {businessHours.map((schedule, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                      <span className="font-medium text-gray-900">{schedule.days}</span>
                      <span className="text-blue-900 font-semibold">{schedule.hours}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-amber-800 text-sm">
                    <strong>Note:</strong> We request 24 hours' notice for long-distance trips, 
                    but we're happy to work with last-minute requests whenever possible.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Service Area */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <MapPin className="h-6 w-6 text-blue-900" />
                  <span>Service Area</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Primary Coverage</h4>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Calhoun County</li>
                      <li>• Battle Creek</li>
                      <li>• Marshall</li>
                      <li>• Albion</li>
                      <li>• Homer</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Extended Services</h4>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Surrounding counties</li>
                      <li>• Long-distance medical facilities</li>
                      <li>• Specialized treatment centers</li>
                      <li>• Airport transportation</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Company Information */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl text-center">About Quality Transport Services</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <Shield className="h-6 w-6 text-blue-900 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Veteran-Owned</h4>
                      <p className="text-gray-600 text-sm">
                        Founded by veterans who understand the values of service, reliability, and integrity.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <Users className="h-6 w-6 text-blue-900 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Family-Operated</h4>
                      <p className="text-gray-600 text-sm">
                        A family business that treats every client with the care and respect they deserve.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <MapPin className="h-6 w-6 text-blue-900 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Locally Focused</h4>
                      <p className="text-gray-600 text-sm">
                        Proudly serving Calhoun County and surrounding areas with personalized service.
                      </p>
                    </div>
                  </div>
                </div>
                <div>
                  <img
                    src="https://customer-assets.emergentagent.com/job_b34784ad-a649-4d8b-9d46-f4aa92c496c6/artifacts/59p6zsqu_NEMT%20LOGO%20Transparent%20%281%29.png"
                    alt="Quality Transport Services Logo"
                    className="w-full max-w-sm mx-auto"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Emergency Contact Notice */}
      <section className="py-12 bg-red-50 border-t-4 border-red-400">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-xl font-bold text-red-800 mb-2">Emergency Medical Transportation</h3>
          <p className="text-red-700 mb-4">
            For emergency medical situations, please call 911 immediately. 
            Quality Transport Services provides non-emergency medical transportation only.
          </p>
          <p className="text-red-600 text-sm">
            If you need urgent medical attention, do not delay - contact emergency services first.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Contact us today to schedule your transportation or learn more about our services. 
            We're here to help you get where you need to go safely and comfortably.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="secondary" className="bg-amber-500 hover:bg-amber-600 text-white">
              <Link to="/book-ride">Book Transportation</Link>
            </Button>
            <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-900">
              <Phone className="h-4 w-4 mr-2" />
              Call (269) 248-3514
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;